import { memo, useId, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsightsSectionHeading from "./InsightsSectionHeading";
import InsightsChartBarWithTooltip from "./InsightsChartBarWithTooltip";
import {
  insightsMaxCountAmongBuckets,
  insightsNormalizedBarHeightPct,
} from "../utils/insightsChartHelpers";
import { formatInsightsMetricDisplay } from "../utils/insightsSetRowMath";
import {
  insightsEffortDistributionBarColorAtIndex,
  INSIGHTS_EFFORT_AMRAP_BAR_COLOR,
} from "../utils/insightsProgramInsightsColors";

/**
 * Histogram of prescribed effort: RPE 5–10 or RiR 5–0 (0.5 steps), depending on coach preference,
 * plus a trailing AMRAP bar for prescribed sets whose RPE/RiR is "AMRAP" instead of a numeric value.
 * Section title follows coach intensity preference (`rpe` vs `rir`).
 *
 * @param {object} props
 * @param {Array<{ step: number|"amrap", count: number }>} props.buckets
 * @param {"rpe"|"rir"} [props.insightsIntensityMeasure]
 * @param {number} [props.syncRowIndex] — `data-insights-sync-row`; same index on this section in every week column for `useProgramInsightsCrossColumnRowSync` (see InsightsWeekColumn).
 */
function InsightsRpeDistributionSection({
  buckets,
  syncRowIndex,
  insightsIntensityMeasure = "rpe",
}) {
  const { t } = useTranslation("athlete-portal");
  const isRir = insightsIntensityMeasure === "rir";

  /** Stable id for this column’s RPE section title. */
  const titleId = useId();

  const maxCount = useMemo(
    () => insightsMaxCountAmongBuckets(buckets),
    [buckets]
  );
  const effortBarTooltipKey = isRir
    ? "programs.programInsights.panel.chartBarTooltipRirSets"
    : "programs.programInsights.panel.chartBarTooltipRpeSets";

  return (
    <section
      className="ProgramInsights-section"
      aria-labelledby={titleId}
      data-insights-sync-row={syncRowIndex}
    >
      <InsightsSectionHeading
        titleId={titleId}
        title={t(
          isRir
            ? "programs.programInsights.panel.sectionRirDistribution"
            : "programs.programInsights.panel.sectionRpeDistribution"
        )}
        infoAriaLabel={t(
          isRir
            ? "programs.programInsights.panel.rirDistributionInfo"
            : "programs.programInsights.panel.rpeDistributionInfo"
        )}
      />
      <div
        className="ProgramInsights-rpeChart"
        role="img"
        aria-label={t(
          isRir
            ? "programs.programInsights.panel.rirGroupAria"
            : "programs.programInsights.panel.rpeGroupAria"
        )}
      >
        {buckets.map((b, barIndex) => {
          const isAmrap = b.step === "amrap";
          const heightPct = insightsNormalizedBarHeightPct(b.count, maxCount);
          const barColor = isAmrap
            ? INSIGHTS_EFFORT_AMRAP_BAR_COLOR
            : insightsEffortDistributionBarColorAtIndex(barIndex);
          const stepLabel = isAmrap
            ? t("programs.programInsights.panel.effortAmrapLabel")
            : formatInsightsMetricDisplay(b.step);
          const tooltipBody =
            b.count > 0
              ? t(effortBarTooltipKey, {
                  value: stepLabel,
                  count: formatInsightsMetricDisplay(b.count),
                })
              : undefined;
          const tooltipId = `${titleId.replaceAll(":", "")}-effort-${stepLabel.replace(".", "_")}`;

          return (
            <div
              key={b.step}
              className={`ProgramInsights-rpeCol${
                isAmrap ? " ProgramInsights-rpeCol--amrap" : ""
              }`}
            >
              <InsightsChartBarWithTooltip
                count={b.count}
                heightPct={heightPct}
                tooltipId={tooltipId}
                tooltipBody={tooltipBody}
                wrapClassName="ProgramInsights-rpeBarWrap"
                barClassName="ProgramInsights-rpeBar"
                barStyle={{ backgroundColor: barColor }}
              />
              <span className="ProgramInsights-rpeLabel">{stepLabel}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

InsightsRpeDistributionSection.propTypes = {
  buckets: PropTypes.arrayOf(
    PropTypes.shape({
      step: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["amrap"])])
        .isRequired,
      count: PropTypes.number.isRequired,
    })
  ).isRequired,
  insightsIntensityMeasure: PropTypes.oneOf(["rpe", "rir"]),
  syncRowIndex: PropTypes.number,
};

export default memo(InsightsRpeDistributionSection);
